import { useSyncExternalStore } from 'react';
import type { IAiConversation } from './ai/IAiConversation';
import type { IAiMessage } from './IAiMessage';

const STORAGE_KEY = 'po.ai.v1';
/** Скільки останніх повідомлень зберігати між перезавантаженнями. */
const KEEP = 60;

let state: IAiConversation = { userId: null, messages: [], busy: false, pending: [], open: false };
const listeners = new Set<() => void>();
const subscribe = (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener); }; };

/** Збережені розмови: ідентифікатор користувача → { відкрита панель, повідомлення } (недоступне сховище — лише пам'ять сторінки). */
function readAll(): Record<string, { open?: boolean; messages?: IAiMessage[] }> {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}');
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, { open?: boolean; messages?: IAiMessage[] }>) : {};
  } catch {
    return {};
  }
}

function update(patch: Partial<IAiConversation>): void {
  state = { ...state, ...patch };
  if (state.userId) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...readAll(), [state.userId]: { open: state.open, messages: state.messages.slice(-KEEP) } }));
    } catch { /* приватний режим — розмова живе до закриття вкладки */ }
  }
  listeners.forEach((l) => l());
}

/** Відновлює розмову користувача (прототип v4 зберігає aiOpen і aiMsgs); повертає, чи панель була відкрита. */
export function restoreAiConversation(userId: string): boolean {
  if (state.userId === userId) return state.open;
  const saved = readAll()[userId] ?? {};
  state = { userId, messages: Array.isArray(saved.messages) ? saved.messages : [], busy: false, pending: [], open: !!saved.open };
  listeners.forEach((l) => l());
  return state.open;
}

/** Додає повідомлення до розмови (розмова живе між сторінками й після закриття панелі — як у прототипі v4). */
export function addAiMessage(message: IAiMessage): void { update({ messages: [...state.messages, message] }); }

/** Нова розмова: очищає повідомлення. */
export function clearAiMessages(): void { update({ messages: [] }); }

/** Асистент «думає» (кнопка «Надіслати» вимкнена, показано «Думаю…»). */
export function setAiBusy(busy: boolean): void { update({ busy }); }

/** Чи відкрита панель — запам'ятовується для наступного завантаження. */
export function rememberAiOpen(open: boolean): void { if (state.open !== open) update({ open }); }

/** Питання з події po:ai-ask («Запитати ШІ» на записі): панель надішле його, щойно буде готова. */
export function queueAiAsk(text: string): void { if (text.trim()) update({ pending: [...state.pending, text.trim()] }); }

/** Забирає найстаріше питання з черги. */
export function takeAiAsk(): string | undefined {
  const [first, ...rest] = state.pending;
  if (first !== undefined) update({ pending: rest });
  return first;
}

/** Поточний стан розмови з ШІ-асистентом. */
export function useAiConversation(): IAiConversation {
  return useSyncExternalStore(subscribe, () => state, () => state);
}

/** Поточні повідомлення розмови з ШІ-асистентом. */
export function useAiMessages(): IAiMessage[] {
  return useAiConversation().messages;
}
